import { ChatConversationMessage, ChatMessageID } from '../models/chat';
import { sortChatMessages } from '../utils/chatUtils';
import { AbstractStore, ActionHandlers, BaseStoreAction } from './abstract.store';
import { chatStore, IChatConversationMessage } from './chat.store';

export type ConversationsStoreState = {
    conversations: { [conversationId: number]: ConversationState };
};

export type ConversationState = {
    messages: ChatConversationMessage[];

    /*
     * Considering our position on the chat history, do we have a next page of messages that we could load from the API?
     *  - false: last API call told us there are NO new messages. We should append any incoming WebSocket messages.
     *  - true:  last API call told us we HAVE more messages. Do NOT append WebSocket messages.
     */
    hasNextMessages: boolean;
    hasPreviousMessages: boolean;
    searchTerm?: string;
};

export class ConversationsStore extends AbstractStore<BaseStoreAction, ConversationsStoreState> {
    private static handleSetSearchTerm(
        state: ConversationsStoreState,
        payload: { conversationId: number; searchTerm: string }
    ) {
        const { conversationId, searchTerm } = payload;
        const newState = { ...state };
        newState.conversations[conversationId] = { ...newState.conversations[conversationId], searchTerm };
        return newState;
    }

    createInitialState(): ConversationsStoreState {
        return {
            conversations: {},
        };
    }

    getActionHandlers(): ActionHandlers<BaseStoreAction, ConversationsStoreState> {
        return {
            set_search_term: ConversationsStore.handleSetSearchTerm,
            set_messages: this.handleSetMessages,
            merge_messages: this.handleMergeMessages,
            remove_message: this.handleRemoveMessage,
            new_message: this.handleNewMessage,
            message_updated: this.handleMessageUpdated,
        };
    }

    getConversation$(conversationId: number) {
        return this.store$.map((s) => s.conversations[conversationId]).filter((conversation) => Boolean(conversation));
    }

    getConversationMessages$(conversationId: number) {
        return this.getConversation$(conversationId).map((conversation) => conversation.messages);
    }

    getOldestMessageId$(conversationId: number): Rx.Observable<ChatMessageID | null> {
        // the first message in array should be the oldest one
        return this.getConversationMessages$(conversationId)
            .take(1)
            .map((messages) => messages?.[0]?.id || null);
    }

    dispatchSetMessages(
        conversationId: number,
        messages: ChatConversationMessage[],
        payload: { hasPreviousMessages?: boolean; hasNextMessages?: boolean }
    ) {
        chatStore.setConversationMessages(conversationId, messages as IChatConversationMessage[], payload);
        this.dispatch({ type: 'set_messages', payload: { conversationId, messages, ...payload } });
    }

    dispatchMergeMessages(conversationId: number, messages: ChatConversationMessage[], hasPreviousMessages: boolean) {
        chatStore.mergeConversationMessages(conversationId, messages as IChatConversationMessage[], {
            hasPreviousMessages,
        });
        this.dispatch({ type: 'merge_messages', payload: { conversationId, messages, hasPreviousMessages } });
    }

    dispatchMessageCreateUpdate(conversationId: number, message: ChatConversationMessage) {
        chatStore.addConversationMessage(conversationId, message as IChatConversationMessage);
        this.dispatch({ type: 'new_message', payload: { conversationId, message } });
    }

    dispatchMessageUpdated(conversationId: number, message: ChatConversationMessage) {
        chatStore.updateConversationMessage(conversationId, message as IChatConversationMessage);
        this.dispatch({ type: 'message_updated', payload: { conversationId, message } });
    }

    dispatchMessageRemove(conversationId: number, message: ChatConversationMessage) {
        chatStore.removeConversationMessage(conversationId, message as IChatConversationMessage);
        this.dispatch({ type: 'remove_message', payload: { conversationId, message } });
    }

    dispatchSetSearchTerm(conversationId: number, searchTerm: string) {
        this.dispatch({ type: 'set_search_term', payload: { conversationId, searchTerm } });
    }

    private log(...args: any[]) {
        console.log.apply(console, ['ConversationsStore', ...args]);
    }

    private handleSetMessages(
        state: ConversationsStoreState,
        payload: {
            conversationId: number;
            messages: ChatConversationMessage[];
            hasPreviousMessages: boolean;
            hasNextMessages: boolean;
        }
    ) {
        const { conversationId, messages, hasPreviousMessages, hasNextMessages } = payload;

        // update conversation with new messages
        const conversation = this.updateConversation(state, conversationId, (conversation) => {
            const sortedMessages = [...messages]
                // sort by id - id is better than created_at as it offers higher precision
                .sort(sortChatMessages);

            return {
                ...conversation,
                messages: sortedMessages,
                hasPreviousMessages,
                hasNextMessages,
            };
        });

        return {
            ...state,
            conversations: {
                ...state.conversations,
                [conversationId]: conversation,
            },
        };
    }

    private handleMergeMessages(
        state: ConversationsStoreState,
        payload: { conversationId: number; messages: ChatConversationMessage[]; hasPreviousMessages: boolean }
    ) {
        const { conversationId, messages, hasPreviousMessages } = payload;

        // create hashmap of new chat message ids
        const newIdsHashmap: { [id: number]: boolean } = messages.reduce(
            (previousValue, currentValue) => ({
                ...previousValue,
                [currentValue.id]: true,
            }),
            {}
        );

        // update conversation with new messages
        const conversation = this.updateConversation(state, conversationId, (conversation) => {
            // delete duplicated messages
            const existingMessages = conversation.messages.filter(
                (message) => newIdsHashmap[message.id as any] !== true
            );
            // if message in messages and existingMessages - use the one from new messages
            const mergedMessages = [...existingMessages, ...messages]
                // sort by id - id is better than created_at as it offers higher precision
                .sort(sortChatMessages);

            return {
                ...conversation,
                messages: mergedMessages,
                hasPreviousMessages,
            };
        });

        return {
            ...state,
            conversations: {
                ...state.conversations,
                [conversationId]: conversation,
            },
        };
    }

    private handleRemoveMessage(
        state: ConversationsStoreState,
        payload: { conversationId: number; message: ChatConversationMessage }
    ) {
        const { conversationId, message } = payload;
        return {
            ...state,
            conversations: {
                ...state.conversations,
                [conversationId]: this.removeMessage(state, conversationId, message),
            },
        };
    }

    private handleNewMessage(
        state: ConversationsStoreState,
        payload: { conversationId: number; message: ChatConversationMessage }
    ) {
        const { conversationId, message } = payload;
        const hasNextMessages = state.conversations[conversationId]?.hasNextMessages ?? false;

        if (hasNextMessages) {
            return state; // we still can fetch messages from the backend, don't append new messages
        }

        // make sure we remove the "optimistic" message to prevent duplicates
        const conversation = this.removeMessage(state, conversationId, message);

        // append new message
        conversation.messages.push(message);

        return {
            ...state,
            conversations: {
                ...state.conversations,
                [conversationId]: conversation,
            },
        };
    }

    private handleMessageUpdated(
        state: ConversationsStoreState,
        payload: { conversationId: number; message: ChatConversationMessage }
    ) {
        const { conversationId, message } = payload;

        return {
            ...state,
            conversations: {
                ...state.conversations,
                [conversationId]: this.updateConversation(state, conversationId, (conversation) => {
                    return {
                        ...conversation,
                        messages: conversation.messages.map((conversationMessage) => {
                            // if id or guid matches, replace the message with new one
                            if (conversationMessage.id === message.id || conversationMessage.guid === message.guid) {
                                return message;
                            }
                            return conversationMessage;
                        }),
                    };
                }),
            },
        };
    }

    private updateConversation(
        state: ConversationsStoreState,
        conversationId: number,
        update: (conversation: ConversationState) => ConversationState
    ) {
        const conv = this.getConversation(state, conversationId);

        return update(conv);
    }

    /**
     * get conversation from state, if conversation doesnt exist - will create a empty one
     * @param state
     * @param conversationId
     * @private
     */
    private getConversation(state: ConversationsStoreState, conversationId: number): ConversationState {
        return (
            state.conversations[conversationId] || {
                messages: [],
                hasPreviousMessages: false,
                hasNextMessages: false,
            }
        );
    }

    /**
     * Removes a message from conversation;
     * Will remove messages having the same id or guid
     * @param state
     * @param conversationId
     * @param message
     * @private
     */
    private removeMessage(state: ConversationsStoreState, conversationId: number, message: ChatConversationMessage) {
        return this.updateConversation(state, conversationId, (conversation) => {
            return {
                ...conversation,
                messages: conversation.messages.filter((m) => {
                    if (m.id === message.id) {
                        return false;
                    }

                    if (!m.guid) {
                        return true;
                    }
                    if (!message.guid) {
                        return true;
                    }
                    return m.guid !== message.guid;
                }),
            };
        });
    }
}

angular.module('tradersyard').service('ConversationsStore', ConversationsStore);
